import type { ModuleBuilderDraft, QuestionOverride } from "./types";
import type { ParsedActivityRow } from "./catalogModel";
import { getQuestionDisplayMeta } from "./catalogModel";
import type { FlatLibraryQuestion } from "./catalogTypes";

const CSV_HEADER = [
  "Type",
  "Activity Title, Instrument Title, Question Title, AnswerOption anchor",
  "Activity Description, Instrument Description, Question Default Prompt, AnswerOption Help Text",
  "Question Self-assessment prompt",
  "Question Help Text",
  "Question input type",
  "Required",
  "Activity Category"
] as const;

// One inherited question, as returned by GET /instrument/{id}. Mirrors the
// shape the get-instrument Lambda emits — kept inline so csvExport stays
// independent of the page-level types.
export type InheritedCsvQuestion = {
  id: string;
  title: string;
  defaultPrompt: string | null;
  selfAssessmentPrompt: string | null;
  helpText: string | null;
  required: boolean | null;
  inputType: string | null;
  answerOptions: Array<{
    id: string;
    anchor: string | null;
    value: string;
    sortingIndex: number | null;
    helpText: string | null;
  }>;
};

function escapeCell(value: string | null | undefined): string {
  const s = (value ?? "").toString();
  if (s === "") return "";
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function row(values: Array<string | null | undefined>): string {
  return values.map(escapeCell).join(",");
}

// Help-text convention: a single-line entry stays as plain text, but the
// moment the user breaks the help text onto a second (or further) line, each
// non-empty line is wrapped in <li>...</li> so the import script can write
// the bullet list directly into the database column. Empty lines are
// dropped so trailing newlines don't create empty <li> items.
function formatHelpTextForCsv(text: string | null | undefined): string {
  const raw = (text ?? "").replace(/\r\n/g, "\n");
  if (!raw.includes("\n")) return raw.trim();
  const lines = raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length <= 1) return lines[0] ?? "";
  return lines.map((line) => `<li>${line}</li>`).join("");
}

function inputTypeLabelFromName(name: string | null | undefined): "Feedback" | "Multi-select" | "Radio" {
  const s = (name ?? "").toLowerCase();
  if (
    s.includes("multi") ||
    s.includes("checkbox") ||
    s.includes("select all") ||
    s.includes("multiple")
  ) {
    return "Multi-select";
  }
  if (
    s.includes("text") ||
    s.includes("narrative") ||
    s.includes("free") ||
    s.includes("comment") ||
    s.includes("feedback")
  ) {
    return "Feedback";
  }
  return "Radio";
}

export function buildCsv(
  draft: ModuleBuilderDraft,
  parsedByInstrumentId: Record<string, ParsedActivityRow[]>,
  flatByQuestionId: Map<string, FlatLibraryQuestion>,
  inheritedQuestionsByInstrumentId: Record<string, InheritedCsvQuestion[]> = {}
): string {
  const lines: string[] = [];
  lines.push(row([...CSV_HEADER]));

  for (let instrIndex = 0; instrIndex < draft.instruments.length; instrIndex++) {
    const instr = draft.instruments[instrIndex]!;
    if (instrIndex > 0) {
      lines.push(""); // blank separator between instrument blocks
    }
    lines.push(
      row([
        "Instrument",
        instr.title.trim(),
        instr.description.trim(),
        "",
        "",
        "",
        ""
      ])
    );

    const assessmentTitle = instr.assessmentSettings.title.trim();
    const assessmentDescription = instr.assessmentSettings.description.trim();
    if (assessmentTitle) {
      lines.push(
        row([
          "Assessment",
          assessmentTitle,
          assessmentDescription,
          "",
          "",
          "",
          ""
        ])
      );
    }

    const activityRows = parsedByInstrumentId[instr.id] ?? [];
    for (const activity of activityRows) {
      const cells: Array<string | null | undefined> = [
        "Activity",
        activity.title,
        activity.description,
        "",
        "",
        "",
        ""
      ];
      const cat = activity.category.trim();
      if (cat) {
        cells.push(cat);
      }
      lines.push(row(cells));
    }

    if (instr.sourceInstrumentId) {
      // Reuse mode: questions are inherited from the source instrument and
      // not editable in the draft. Emit them straight from the loaded detail.
      // Callers must gate Export on readiness — computeReadiness blocks while
      // the source detail is still loading or has failed, so by the time we
      // reach here the inherited array is expected to be populated.
      const inherited = inheritedQuestionsByInstrumentId[instr.id] ?? [];
      for (const q of inherited) {
        const inputTypeLabel = inputTypeLabelFromName(q.inputType ?? null);
        const requiredString = q.required === false ? "FALSE" : "TRUE";
        lines.push(
          row([
            "Question",
            q.title,
            q.defaultPrompt ?? "",
            q.selfAssessmentPrompt ?? "",
            formatHelpTextForCsv(q.helpText),
            inputTypeLabel,
            requiredString
          ])
        );
        if (inputTypeLabel === "Feedback") continue;
        for (const opt of q.answerOptions) {
          const label = (opt.anchor && opt.anchor.trim()) || opt.value;
          lines.push(
            row([
              "AnswerOption",
              label,
              formatHelpTextForCsv(opt.helpText),
              "",
              "",
              "",
              ""
            ])
          );
        }
      }
      continue;
    }

    const qids = draft.instrumentQuestionIdsById[instr.id] ?? [];
    const requiredById = draft.instrumentQuestionRequiredById[instr.id] ?? {};
    const overridesById = draft.instrumentQuestionOverridesById[instr.id] ?? {};
    const answerSetById = draft.instrumentQuestionAnswerSetById[instr.id] ?? {};

    for (const qid of qids) {
      const override: QuestionOverride | undefined = overridesById[qid];
      const meta = getQuestionDisplayMeta(
        qid,
        flatByQuestionId,
        override ? { [qid]: override } : {},
        answerSetById[qid]
      );

      const inputTypeName =
        meta.kind === "feedback" ? "Feedback" : meta.kind === "multi" ? "Multi-select" : "Radio";

      const requiredFlag = requiredById[qid];
      const requiredString = requiredFlag === undefined ? "TRUE" : requiredFlag ? "TRUE" : "FALSE";

      lines.push(
        row([
          "Question",
          meta.title,
          meta.prompt,
          override?.selfAssessmentPrompt?.trim() ?? "",
          formatHelpTextForCsv(override?.helpText ?? ""),
          inputTypeName,
          requiredString
        ])
      );

      // The structured options on the override (if any) are the source of
      // truth for both label/value AND per-option help text. When no override
      // is set, fall back to meta.answers (which already carries the catalog's
      // helpText as `prompt`).
      const answersForCsv: { label: string; value: string; helpText: string }[] =
        meta.kind === "feedback"
          ? []
          : override?.options !== undefined
            ? override.options.map((o) => ({
                label: o.label,
                value: o.value,
                helpText: o.helpText ?? ""
              }))
            : meta.answers.map((a) => ({
                label: a.label,
                value: a.value,
                helpText: a.prompt ?? ""
              }));

      for (const opt of answersForCsv) {
        lines.push(
          row(["AnswerOption", opt.label, formatHelpTextForCsv(opt.helpText), "", "", "", ""])
        );
      }
    }
  }

  return lines.join("\n");
}

export function downloadCsv(filename: string, contents: string): void {
  if (typeof window === "undefined") return;
  const blob = new Blob([contents], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export function suggestedFilename(moduleTitle: string): string {
  const safe = (moduleTitle.trim() || "module")
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_|_$/g, "");
  return `${safe || "module"}.csv`;
}
